// Phase 0.4 (grade-2 ceiling) — declarative visual-scaffold registry.
//
// THE PROBLEM (see PLAN-curriculum-ceiling-grade2.md, Phase 0 item 4):
// MathVisual used to hardcode an inline switch over ten_frame / groups /
// number_line, so adding a new concept visual (array, place-value-blocks,
// fraction) meant editing MathVisual itself — the session screens and the
// renderer were coupled to the closed set of kinds.
//
// THE FIX: a declarative registry that maps a visual `kind` key -> a React
// renderer component (+ optional metadata). MathVisual resolves its
// renderer from here instead of branching inline. A NEW concept visual
// REGISTERS itself (one `registerRenderer(...)` call, typically colocated
// with the renderer) and plugs in with NO edit to MathVisual or the
// session screens. Phase 2/3 visuals are therefore purely additive.
//
// EXTENSION POINT — to add e.g. an `array` renderer:
//
//   // src/components/renderers/ArrayRenderer.tsx
//   import { registerRenderer, type MathRendererProps } from "./rendererRegistry";
//   function ArrayRenderer(props: MathRendererProps) { return <svg/>; }
//   registerRenderer("array", ArrayRenderer, { label: "Array model" });
//
// then import that module once (e.g. from a renderers/index barrel, or add
// the import beside the built-ins below) so the side-effecting registration
// runs. No switch to edit, no screen to touch.
import { useId, type ComponentType } from "react";
// Phase 2 additive concept renderers (live in their own files; registered
// below beside the built-ins so they plug in with NO edit to MathVisual or
// the session screens — per the EXTENSION POINT note above). They take a
// type-only dependency on this module, so there is no runtime import cycle.
import ArrayRenderer from "./ArrayRenderer";
import PlaceValueBlocksRenderer from "./PlaceValueBlocksRenderer";
import PlaceValueComparisonRenderer from "./PlaceValueComparisonRenderer";
import FractionBarRenderer from "./FractionBarRenderer";
import FractionComparisonRenderer from "./FractionComparisonRenderer";
import ClockRenderer from "./ClockRenderer";
import ElapsedTimeClockRenderer from "./ElapsedTimeClockRenderer";
import CoinsRenderer from "./CoinsRenderer";
import TakeAwayRenderer from "./TakeAwayRenderer";
import TakeAwayBlankRenderer from "./TakeAwayBlankRenderer";
import DivisionSharingRenderer from "./DivisionSharingRenderer";
import DiscreteClustersRenderer from "./DiscreteClustersRenderer";
import DotPatternRenderer from "./DotPatternRenderer";
import NumeralCardRenderer from "./NumeralCardRenderer";
import TwoColorTenFrameRenderer from "./TwoColorTenFrameRenderer";
import TenFrameSubRenderer from "./TenFrameSubRenderer";
import TwoTenFramesSubRenderer from "./TwoTenFramesSubRenderer";
import TwoTenFramesAddRenderer from "./TwoTenFramesAddRenderer";
import ColumnAddRenderer from "./ColumnAddRenderer";
import SkipCountLineRenderer from "./SkipCountLineRenderer";
import CountOnLineRenderer from "./CountOnLineRenderer";
import ShapeArrayRenderer from "./ShapeArrayRenderer";
import PatternStripRenderer from "./PatternStripRenderer";
import NumberBondRenderer from "./NumberBondRenderer";
// MA-690 (slice ms-math-05-family-pictures) — the three part-whole pictures
// ms.math.05/06 items name ("split frame", "set picture", "part strip").
import { SplitTenFrameRenderer, SetPictureRenderer, PartStripRenderer } from "../visuals/PartWholePictures";
// MA-625 — staged add/sub renderers (concrete->bridging->abstract). See
// mathVisualStage.ts for the stage projection that routes to these kinds.
import { CountOnRenderer, CountBackRenderer, EquationRenderer, OperandCaption, operandCaptionSize, CAPTION_CARD } from "./StagedMathRenderers";
// MA-720 — the shared first/second-operand treatment (orange solid vs green
// with an ink outline); defined once, never as per-file literals.
import { OPERAND_A_FILL, OPERAND_B_FILL, OPERAND_A_MARK, OPERAND_B_MARK, operandBOutlineAttr } from "./operandPalette";

/** Shared prop contract every math visual renderer receives. Mirrors the
 *  structured `mathVisual` payload built in contentToEngineItem.ts. New
 *  renderers read whatever subset they need; unused fields are ignored. */
export interface MathRendererProps {
  a?: number;
  b?: number;
  op?: "+" | "-";
  total: number;
  /** MA-C2 (ADR-015) — optional semantic glyph for COUNTING manipulatives
   *  ("star"; MA-690 adds "block", then bear/apple/leaf/shell/fish/button/
   *  duck). Absent or unknown => the legacy generic
   *  circle (byte-identical output). */
  glyph?: string;
  /** MA-C18 — arrangement FAMILY for the `dot_pattern` renderer ("dice" |
   *  "frame" | "generic"). Absent / unknown => the generic dot cluster.
   *  Selects layout only; the drawn COUNT is always keyed off `total`, so an
   *  unknown value can never change the count (answer-safety is family-invariant).
   *  MA-692 — also the `ten_frame` fill arrangement ("pairs",
   *  TEN_FRAME_FILL_ORDER); absent / unknown => the standard fill. */
  arrangement?: string;
  /** MA-C20 — authored fill color for the `two_color_ten_frame` renderer
   *  ("purple" | "orange" | "teal" | "red" | "green" | "blue"). Cosmetic
   *  only; the drawn COUNT is always `a`, so an unrecognized/absent color
   *  can never affect answer-safety (falls back to a neutral fill). */
  color?: string;
  /** MA-C21 — authored shape name for the `shape_array` renderer
   *  ("circle" | "square" | "triangle" | "rectangle"). The visual IS the
   *  question (read-the-shape); the mapper only emits this kind when
   *  `shape` already equals the item's correct answer. */
  shape?: string;
  /** MA-C21 — authored display FAMILY for the `shape_array` renderer
   *  ("outline" | "small" | "large" | "filled" | "solid" | "thick outline").
   *  Cosmetic drawing variation only (shape-constancy pedagogy); never
   *  changes which shape is drawn.
   *  MA-694 — ALSO read by `place_value_blocks`: the value "subject_stated"
   *  (PLACE_VALUE_SUBJECT_STATED) marks a picture whose number the prompt
   *  states, and switches its aria to a count-free, numeral-free sentence.
   *  Not a shape family. */
  displayStyle?: string;
  /** MA-C21 — authored `shown` sequence for the `pattern_strip` renderer
   *  (e.g. ["red circle", "blue square", "red circle"]). The renderer draws
   *  ONLY these tiles + a "?" tile for the next (hidden) element — the
   *  mapper structurally never forwards the answer. */
  sequence?: string[];
  /** MA-C15 (ADR-067) — one {a,b} numerator/denominator pair per shuffled
   *  answer option, for the `fraction_comparison` renderer. Absent for every
   *  other kind. */
  fractions?: { a: number; b: number }[];
  /** MA-C16 — literal named coin multiset for the `coins` renderer
   *  (quarter/dime/nickel/penny counts). When present, the renderer draws
   *  EXACTLY this multiset instead of greedy-splitting `total`. Absent =>
   *  legacy greedy-split behavior, byte-identical to before. */
  coinCounts?: { quarter?: number; dime?: number; nickel?: number; penny?: number };
  /** MA-C17 — exactly 2 {a,b} (hours,minutes) pairs — [0]=start, [1]=end —
   *  for the `elapsed_time_clock` renderer. The renderer reads ONLY this
   *  field, never `total` (the elapsed minutes, carried for contract parity
   *  only), so the elapsed duration can never be rendered. Absent for every
   *  other kind. */
  times?: { a: number; b: number }[];
  /** MA-703 — which slot is the authored blank for the `take_away_blank`
   *  renderer ("minuend" | "subtrahend"). Absent for every other kind. */
  blank?: "minuend" | "subtrahend";
  /** MA-706 — the `skip_count_line` renderer's shown-term sequence, in
   *  display order; `null` marks the blank stop's POSITION only, never its
   *  value. Absent for every other kind. */
  terms?: (number | null)[];
  /** MA-706 — the `skip_count_line` renderer's constant step magnitude
   *  between consecutive stops. Absent for every other kind. */
  step?: number;
}

/** Optional, non-behavioral metadata carried alongside a renderer. */
interface RendererMeta {
  /** Human-readable label (debugging / future picker UI). */
  label?: string;
}

type MathRenderer = ComponentType<MathRendererProps>;

interface RendererEntry {
  component: MathRenderer;
  meta: RendererMeta;
}

const REGISTRY = new Map<string, RendererEntry>();

/** Register (or override) the renderer for a visual `kind` key.
 *  Idempotent per key — a later registration replaces an earlier one,
 *  which lets a feature-specific renderer supersede a built-in if needed. */
export function registerRenderer(
  key: string,
  component: MathRenderer,
  meta: RendererMeta = {},
): void {
  REGISTRY.set(key, { component, meta });
}

/** Resolve the renderer for a `kind` key. Returns `null` when the key is
 *  unknown — callers MUST fail safe (render nothing / a fallback) rather
 *  than throw, so an unmapped kind degrades gracefully instead of crashing
 *  a session. (MathVisual layers the legacy `groups` fallback on top.) */
export function getRenderer(key: string): MathRenderer | null {
  return REGISTRY.get(key)?.component ?? null;
}

/** Resolve a renderer's optional metadata, or null when unknown. */
export function getRendererMeta(key: string): RendererMeta | null {
  return REGISTRY.get(key)?.meta ?? null;
}

/** Whether a `kind` key has a registered renderer. */
export function hasRenderer(key: string): boolean {
  return REGISTRY.has(key);
}

/** All registered keys (test/introspection helper). */
export function registeredKeys(): string[] {
  return [...REGISTRY.keys()];
}

// ── Built-in renderers (extracted verbatim from the old MathVisual inline
//    switch — behavior-preserving). These register themselves at module
//    load. Importing rendererRegistry (which MathVisual does) is enough to
//    populate the legacy ten_frame / groups / number_line keys.

// MA-720 — operand colours + B's outline come from ./operandPalette.
type OperandMark = typeof OPERAND_A_MARK | typeof OPERAND_B_MARK;

// MA-C2 (ADR-015) — a COUNTING manipulative can draw the NAMED object instead
// of a generic circle (e.g. a star for "Count the stars"). A glyph is exactly
// ONE SVG node, drawn at the SAME cx/cy anchor and fill token as the <circle>
// it replaces, so the count-integrity guard (one drawn node per object) stays
// valid and the no-glyph path stays byte-identical. `data-glyph` lets the guard
// tally stars as well as circles. A 5-point star whose OUTER radius matches the
// circle radius keeps the visual weight/footprint identical.
function starPathD(cx: number, cy: number, outer: number): string {
  const inner = outer * 0.5;
  const pts: string[] = [];
  for (let i = 0; i < 10; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const ang = (-90 + i * 36) * (Math.PI / 180);
    pts.push(`${(cx + r * Math.cos(ang)).toFixed(2)},${(cy + r * Math.sin(ang)).toFixed(2)}`);
  }
  return `M${pts.join("L")}Z`;
}

// MA-690 — a "block" (for "How many blocks in all?") is ONE filled rounded
// square, the same shape shapeGlyphs.tsx draws for `square`, centred on the
// circle's anchor with the circle's fill token. Its half-side is 0.85 of the
// circle radius so its footprint matches the circle's, not exceeds it. One
// node per object, tallied by `data-glyph` exactly like the star.
function blockGlyph(key: number | undefined, cx: number, cy: number, r: number, mark: OperandMark) {
  const h = Math.round(r * 0.85);
  return <rect key={key} x={cx - h} y={cy - h} width={2 * h} height={2 * h} rx={Math.max(2, Math.round(h / 4))} {...mark} data-glyph="block" />;
}

// MA-690 (slice count-glyph-art) — seven more named-object count glyphs for
// the nouns ms.math.02 prompts name: bear (teddy face), apple, leaf, shell
// (scallop), fish, button and duck. Each is ONE <path> node (one node per
// object, so the count-integrity guards still tally exactly one mark per
// object), drawn in a unit box scaled by the circle's radius at the circle's
// anchor, in the operand mark's fill. The details (eyes, button holes, leaf
// vein, shell ribs, duck wing) are cut-outs inside the same path: under the
// default nonzero fill rule every filled ring is wound clockwise and every
// hole anticlockwise; `ringsToD` re-winds each ring from its signed area so
// a hand-written ring can never flip. Each hole sits inside exactly one
// filled ring (never over an overlap), so it always reads as a hole. No text,
// no numerals. "stones" has NO glyph on purpose: every stone silhouette tried
// read as bread or a potato in one flat fill, and a wrong object is worse
// than the generic counter (docs/build/specs/MA-690-count-glyph-art.md).
type Pt = readonly [number, number];
type Seg = { c?: readonly [Pt, Pt]; p: Pt }; // cubic when c is set, else a line
type Ring = { start: Pt; segs: Seg[]; hole?: boolean };

const K = 0.5523;
function ellipseRing(cx: number, cy: number, rx: number, ry: number, hole = false): Ring {
  return { hole, start: [cx, cy - ry], segs: [
    { c: [[cx + K * rx, cy - ry], [cx + rx, cy - K * ry]], p: [cx + rx, cy] },
    { c: [[cx + rx, cy + K * ry], [cx + K * rx, cy + ry]], p: [cx, cy + ry] },
    { c: [[cx - K * rx, cy + ry], [cx - rx, cy + K * ry]], p: [cx - rx, cy] },
    { c: [[cx - rx, cy - K * ry], [cx - K * rx, cy - ry]], p: [cx, cy - ry] },
  ] };
}
const circleRing = (cx: number, cy: number, r: number, hole = false) => ellipseRing(cx, cy, r, r, hole);
function polyRing(pts: Pt[], hole = false): Ring {
  return { hole, start: pts[0], segs: pts.slice(1).map((p) => ({ p })) };
}
function bez(a: Pt, b: Pt, c: Pt, d: Pt, t: number): Pt {
  const u = 1 - t;
  return [u*u*u*a[0] + 3*u*u*t*b[0] + 3*u*t*t*c[0] + t*t*t*d[0], u*u*u*a[1] + 3*u*u*t*b[1] + 3*u*t*t*c[1] + t*t*t*d[1]];
}
/** Signed area (screen coords, y down): > 0 means clockwise on screen. */
function ringArea(r: Ring): number {
  const pts: Pt[] = [r.start];
  let prev = r.start;
  for (const s of r.segs) {
    if (s.c) for (let i = 1; i <= 8; i++) pts.push(bez(prev, s.c[0], s.c[1], s.p, i / 8));
    else pts.push(s.p);
    prev = s.p;
  }
  let a = 0;
  for (let i = 0; i < pts.length; i++) { const p = pts[i], q = pts[(i + 1) % pts.length]; a += p[0] * q[1] - q[0] * p[1]; }
  return a / 2;
}
function reverseRing(r: Ring): Ring {
  const nodes: Pt[] = [r.start, ...r.segs.map((s) => s.p)];
  const segs: Seg[] = [];
  for (let i = r.segs.length - 1; i >= 0; i--) {
    const s = r.segs[i];
    segs.push(s.c ? { c: [s.c[1], s.c[0]], p: nodes[i] } : { p: nodes[i] });
  }
  return { hole: r.hole, start: nodes[nodes.length - 1], segs };
}
function ringsToD(rings: Ring[], cx: number, cy: number, r: number): string {
  const f = (p: Pt) => `${(cx + p[0] * r).toFixed(2)},${(cy + p[1] * r).toFixed(2)}`;
  return rings.map((ring0) => {
    const cw = ringArea(ring0) > 0;
    const ring = cw === !ring0.hole ? ring0 : reverseRing(ring0);
    return `M${f(ring.start)}` + ring.segs.map((s) => (s.c ? `C${f(s.c[0])} ${f(s.c[1])} ${f(s.p)}` : `L${f(s.p)}`)).join("") + "Z";
  }).join("");
}

// scallop
function shellRings(): Ring[] {
  const H: Pt = [0, 0.62], R = 1.5, half = 40 * Math.PI / 180, N = 5;
  const at = (ang: number, rad: number): Pt => [H[0] + rad * Math.sin(ang), H[1] - rad * Math.cos(ang)];
  const segs: Seg[] = [];
  const left = at(-half, R);
  for (let i = 0; i < N; i++) {
    const a0 = -half + (2 * half * i) / N, a1 = -half + (2 * half * (i + 1)) / N;
    const p1 = at(a1, R);
    const c0 = at(a0 + (a1 - a0) * 0.1, R + 0.15), c1 = at(a1 - (a1 - a0) * 0.1, R + 0.15);
    segs.push({ c: [c0, c1], p: p1 });
  }
  const fan: Ring = { start: [-0.16, 0.72], segs: [{ p: left }, ...segs, { p: [0.16, 0.72] }] };
  const hinge = polyRing([[-0.42, 0.62], [0.42, 0.62], [0.3, 0.92], [-0.3, 0.92]]);
  const ribs: Ring[] = [];
  for (let i = 1; i < N; i++) {
    const a = -half + (2 * half * i) / N;
    const w = 0.035;
    ribs.push(polyRing([at(a, 0.45), at(a + w, R * 0.9), at(a - w, R * 0.9)], true));
  }
  return [fan, hinge, ...ribs];
}

const COUNT_GLYPH_RINGS: Record<string, () => Ring[]> = {
  bear: () => [
    circleRing(-0.6, -0.55, 0.32), circleRing(0.6, -0.55, 0.32),
    circleRing(0, 0.1, 0.8),
    circleRing(-0.3, -0.05, 0.11, true), circleRing(0.3, -0.05, 0.11, true),
    ellipseRing(0, 0.4, 0.34, 0.25, true), ellipseRing(0, 0.33, 0.13, 0.09),
  ],
  apple: () => [
    { start: [0, -0.48], segs: [
      { c: [[0.3, -0.8], [0.95, -0.7]], p: [0.9, 0.02] },
      { c: [[0.86, 0.58], [0.48, 0.96]], p: [0.2, 0.88] },
      { c: [[0.1, 0.85], [-0.1, 0.85]], p: [-0.2, 0.88] },
      { c: [[-0.48, 0.96], [-0.86, 0.58]], p: [-0.9, 0.02] },
      { c: [[-0.95, -0.7], [-0.3, -0.8]], p: [0, -0.48] },
    ] },
    polyRing([[-0.08, -0.5], [-0.02, -0.98], [0.1, -0.96], [0.07, -0.5]]),
    { start: [0.12, -0.76], segs: [{ c: [[0.3, -1.02], [0.6, -1.0]], p: [0.72, -0.86] }, { c: [[0.55, -0.66], [0.3, -0.64]], p: [0.12, -0.76] }] },
    ellipseRing(-0.5, -0.18, 0.1, 0.2, true),
  ],
  leaf: () => [
    { start: [-0.55, 0.55], segs: [
      { c: [[-0.78, -0.3], [-0.05, -0.88]], p: [0.88, -0.88] },
      { c: [[0.88, 0.05], [0.3, 0.78]], p: [-0.55, 0.55] },
    ] },
    polyRing([[-0.52, 0.5], [-0.46, 0.58], [-0.9, 0.98], [-0.98, 0.9]]),
    polyRing([[-0.42, 0.36], [0.62, -0.66], [-0.36, 0.44]], true),
  ],
  shell: shellRings,
  fish: () => [
    { start: [0.95, 0.02], segs: [
      { c: [[0.72, -0.78], [-0.22, -0.8]], p: [-0.45, 0.02] },
      { c: [[-0.22, 0.82], [0.72, 0.8]], p: [0.95, 0.02] },
    ] },
    polyRing([[-0.3, 0.02], [-0.98, -0.62], [-0.82, 0.02], [-0.98, 0.66]]),
    circleRing(0.5, -0.16, 0.11, true),
  ],
  button: () => [
    circleRing(0, 0, 0.92), circleRing(0, 0, 0.76, true), circleRing(0, 0, 0.66),
    circleRing(-0.22, -0.22, 0.11, true), circleRing(0.22, -0.22, 0.11, true),
    circleRing(-0.22, 0.22, 0.11, true), circleRing(0.22, 0.22, 0.11, true),
  ],
  duck: () => [
    ellipseRing(-0.05, 0.38, 0.88, 0.46),
    polyRing([[-0.98, -0.18], [-0.55, 0.1], [-0.9, 0.38]]),
    circleRing(0.38, -0.36, 0.4),
    polyRing([[0.68, -0.44], [1.0, -0.3], [0.68, -0.2]]),
    circleRing(0.47, -0.46, 0.08, true),
    { hole: true, start: [-0.55, 0.28], segs: [{ c: [[-0.35, 0.68], [0.15, 0.68]], p: [0.3, 0.3] }, { c: [[0.1, 0.52], [-0.3, 0.52]], p: [-0.55, 0.28] }] },
  ],
};

/** MA-690 — the path `d` of a named count glyph at (cx, cy), radius r, or
 *  undefined for any token without art (the caller then draws the circle). */
export function countGlyphPathD(glyph: string | undefined, cx: number, cy: number, r: number): string | undefined {
  if (!glyph || !Object.prototype.hasOwnProperty.call(COUNT_GLYPH_RINGS, glyph)) return undefined;
  return ringsToD(COUNT_GLYPH_RINGS[glyph](), cx, cy, r);
}

/** MA-690 — the plural noun a Groups aria-label names for each glyph. */
const GLYPH_PLURAL: Readonly<Record<string, string>> = {
  star: "stars", block: "blocks", bear: "bears", apple: "apples", leaf: "leaves",
  shell: "shells", fish: "fish", button: "buttons", duck: "ducks",
};
function glyphPlural(glyph: string | undefined): string | undefined {
  return glyph && Object.prototype.hasOwnProperty.call(GLYPH_PLURAL, glyph) ? GLYPH_PLURAL[glyph] : undefined;
}

/** One count mark: the star, the block, a named-object glyph, else the
 *  generic circle (fail-closed for an unknown token). */
function countMark(key: number | undefined, glyph: string | undefined, cx: number, cy: number, r: number, mark: OperandMark) {
  if (glyph === "star") return <path key={key} d={starPathD(cx, cy, r)} {...mark} data-glyph="star" />;
  if (glyph === "block") return blockGlyph(key, cx, cy, r, mark);
  const d = countGlyphPathD(glyph, cx, cy, r);
  if (d) return <path key={key} d={d} {...mark} data-glyph={glyph} />;
  return <circle key={key} cx={cx} cy={cy} r={r} {...mark} />;
}

function Dots({ n, mark, startKey = 0, glyph }: { n: number; mark: OperandMark; startKey?: number; glyph?: string }) {
  return (
    <>
      {Array.from({ length: Math.max(0, n) }).map((_, i) => {
        const cx = 18 + (i % 5) * 34;
        const cy = 18 + Math.floor(i / 5) * 34;
        return countMark(startKey + i, glyph, cx, cy, 13, mark);
      })}
    </>
  );
}

/** MA-692 twin-picture-variety (2026-10-07) — the ten frame's FILL
 *  ARRANGEMENTS beyond the standard one (top row left to right, then the
 *  bottom row). The order each family fills the 10 cells in (cell i sits at
 *  column i % 5, row floor(i / 5)):
 *    • "pairs" — column by column, top then bottom (the "pairs" ten frame:
 *                6 reads as three full columns). A scattered fill was
 *                dropped (RED round 1 MUST-1): it breaks the 5-and-10 read.
 *  Layout only: every family fills exactly the first `n` cells of its order,
 *  so the drawn count is always `n` (answer-safety unchanged). An unknown or
 *  absent arrangement is the standard fill. */
export const TEN_FRAME_FILL_ORDER: Readonly<Record<string, readonly number[]>> = {
  pairs: [0, 5, 1, 6, 2, 7, 3, 8, 4, 9],
};

/** MA-692 twin-picture-variety d (2026-10-17) — the VERTICAL ten frame: the
 *  same ten cells drawn as 2 columns x 5 rows. It fills the first column top
 *  to bottom, then the second (cell i sits at column floor(i / 5), row i % 5),
 *  so 5 is one full column and 7 is a full column and 2 more — the frame's
 *  5-and-10 read is kept (part c RED D1: no scattered fill). A single-colour
 *  frame only; drawn at a capped width (96 px, 128 px at md+) so the session
 *  keeps its one-page fit. */
export const TEN_FRAME_VERTICAL = "vertical";
export const TEN_FRAME_VERTICAL_MAX_WIDTH = 96;
/** RED round 1 S1 (2026-10-17): at md+ (the iPad fit path) the vertical frame
 *  gets a larger budget, so its counters are not far smaller than the
 *  horizontal frame's after the session's fit scale. The class literal below
 *  must keep these two numbers (Tailwind reads the literal). */
export const TEN_FRAME_VERTICAL_MAX_WIDTH_MD = 128;
const TEN_FRAME_VERTICAL_CLASS = "max-w-[96px] md:max-w-[128px]";

/** The top-left corner of cell `i` (0..9) in viewBox units: the standard /
 *  pairs frame is 5 columns x 2 rows, the vertical frame 2 columns x 5 rows.
 *  Pure. */
export function tenFrameCellXY(i: number, arrangement?: string): { x: number; y: number } {
  return arrangement === TEN_FRAME_VERTICAL
    ? { x: 6 + Math.floor(i / 5) * 70, y: 6 + (i % 5) * 70 }
    : { x: 6 + (i % 5) * 70, y: 6 + Math.floor(i / 5) * 70 };
}

/** The sorted cell indices a single-colour ten frame of `n` fills in
 *  `arrangement` (the standard fill for an unknown/absent family). Pure. */
export function tenFrameFilledCells(n: number, arrangement?: string): number[] {
  const k = Math.max(0, Math.min(10, Math.floor(Number(n)) || 0));
  const order = arrangement && Object.prototype.hasOwnProperty.call(TEN_FRAME_FILL_ORDER, arrangement)
    ? TEN_FRAME_FILL_ORDER[arrangement]
    : [0, 1, 2, 3, 4, 5, 6, 7, 8, 9];
  return order.slice(0, k).slice().sort((x, y) => x - y);
}

function TenFrame({ a, b, total, glyph, arrangement }: MathRendererProps) {
  const fillA = a ?? total;
  const fillB = b ?? 0;
  const cells = Array.from({ length: 10 });
  // MA-692 — a single-colour frame (no operand B) may be drawn in a fill
  // arrangement; a two-operand frame always keeps the standard fill.
  const arranged = fillB === 0 && !!arrangement && Object.prototype.hasOwnProperty.call(TEN_FRAME_FILL_ORDER, arrangement)
    ? new Set(tenFrameFilledCells(fillA, arrangement))
    : null;
  // MA-692 d — the vertical frame (single colour only; it fills the first n
  // cells, column-first, in tenFrameCellXY's 2 x 5 geometry).
  const vertical = fillB === 0 && arrangement === TEN_FRAME_VERTICAL;
  // MA-728 — the mark operand B is drawn with (read by the hidden
  // data-operand-b-outline attribute below, so the attribute reports the
  // props actually drawn) and how many operand-B cells this frame draws.
  const markB = OPERAND_B_MARK;
  const isBCell = (i: number) => i >= fillA && i < fillA + fillB;
  const operandBDrawn = cells.filter((_, i) => isBCell(i)).length;
  return (
    <svg viewBox={vertical ? "0 0 152 362" : "0 0 360 152"} width="100%"
      className={vertical ? TEN_FRAME_VERTICAL_CLASS : undefined}
      style={vertical ? undefined : { maxWidth: 360 }} role="img"
      // MA-116 (RED-5): every live emission fills exactly `total` (=== the
      // answer) cells — a spoken count here reads the answer aloud to AT
      // users. Generic, count-free label, matching DotPatternRenderer's
      // established answer-safety precedent.
      aria-label="Ten frame"
      // MA-728 — hidden capture hook, "true"/"false"/absent only.
      data-operand-b-outline={operandBOutlineAttr(markB, operandBDrawn)}
      // MA-692 d — hidden capture hook: "vertical" on the 2 x 5 frame, absent otherwise.
      data-ten-frame-orientation={vertical ? TEN_FRAME_VERTICAL : undefined}>
      {cells.map((_, i) => {
        const filled = arranged ? arranged.has(i) : i < fillA + fillB;
        const isB = arranged ? false : isBCell(i);
        const { x, y } = tenFrameCellXY(i, vertical ? TEN_FRAME_VERTICAL : undefined);
        return (
          <g key={i}>
            <rect x={x} y={y} width="68" height="68" rx="8" fill="none" stroke="var(--color-border-secondary, #cdbfae)" strokeWidth="2" />
            {filled && countMark(undefined, glyph, x + 34, y + 34, 22, isB ? markB : OPERAND_A_MARK)}
          </g>
        );
      })}
    </svg>
  );
}

// MA-647 — space between the operand caption and the two dot groups, only
// drawn on the `hasTwo` (two-addend) arm; mirrors TakeAwayRenderer's own gap.
const GROUPS_CAPTION_GAP = 14;

// MA-CLOSURE3 (MA-647 caption probe) — the EXACT condition Groups uses to
// decide whether it draws an operand caption at all, pulled out to its own
// export so `operandCaptionShownForKind` below (the MathSession hook) reads
// the SAME boolean Groups' own render uses instead of re-deriving a second
// copy of "hasTwo" that could drift from it. Groups itself now calls this
// function rather than inlining the expression.
export function groupsHasTwoOperands(props: Pick<MathRendererProps, "a" | "b">): boolean {
  return props.a !== undefined && props.b !== undefined && (props.b as number) > 0;
}

function Groups({ a, b, total, glyph }: MathRendererProps) {
  const hasTwo = groupsHasTwoOperands({ a, b });
  const left = hasTwo ? a! : total;
  const rows = (n: number) => Math.ceil(Math.max(1, n) / 5);
  const h = 36 + (Math.max(rows(left), hasTwo ? rows(b!) : 1) - 1) * 34;
  const gw = (n: number) => 36 + (Math.min(5, Math.max(1, n)) - 1) * 34;
  const dotsW = hasTwo ? gw(left) + 50 + gw(b!) : gw(left) + 12;
  // MA-728 — the mark the second group is drawn with (read by the hidden
  // data-operand-b-outline attribute below) and how many marks it draws
  // (Dots draws Math.floor of a fractional count — Array.from truncates —
  // so a b in (0,1) draws no mark and the attribute is omitted).
  const markB = OPERAND_B_MARK;
  const operandBDrawn = hasTwo ? Math.floor(Math.max(0, b!)) : 0;

  // MA-647 — ONLY the hasTwo (a and b) branch draws the operand caption:
  // the single-group "count the objects" branch has no second operand, so
  // there is no equation to model — it stays exactly as before.
  //
  // Groups renders at a FIXED maxWidth 360 with width="100%" (unlike
  // TakeAwayRenderer, which always renders at scale 1 because its SVG is
  // narrow enough to never hit that cap) — so the whole viewBox is scaled
  // by 360/svgW at display time. A caption sized in constant viewBox units
  // would therefore render at a different CSS px size on every item (up to
  // 2.5x on a small "1+1" item) and would also widen svgW enough to shrink
  // the dots. Scale-compensate: size the caption in viewBox units
  // proportional to dotsW (BEFORE the caption can widen it) so its RENDERED
  // height stays a constant ~52px, matching TakeAwayRenderer, and svgW stays
  // exactly dotsW (the dots never shrink).
  const capScale = dotsW / 360;
  const capCardSize = Math.round(CAPTION_CARD * capScale);
  const capOpW = Math.round(30 * capScale);
  const capGap = GROUPS_CAPTION_GAP * capScale;
  const cap = hasTwo ? operandCaptionSize(capCardSize, capOpW) : null;
  const svgW = dotsW;
  const svgH = cap ? cap.height + capGap + h : h;
  const dotsOffsetX = 0;
  const capOffsetX = cap ? (svgW - cap.width) / 2 : 0;
  const dotsOffsetY = cap ? cap.height + capGap : 0;

  return (
    <svg viewBox={`0 0 ${svgW} ${svgH}`} width="100%"
      style={{ maxWidth: hasTwo ? 360 : 220 }} role="img"
      // MA-116 (RED-5): the single-group case draws exactly `left` (===
      // total === the answer) dots — naming the count reads the answer
      // aloud. The two-group case is unchanged: it names the two ADDENDS,
      // never their sum, so it discloses nothing beyond what a sighted
      // child already gets from counting the two drawn groups.
      aria-label={hasTwo
        // MA-690 — the two-group label names the drawn object (the addends
        // only, never their sum — unchanged rule) when a glyph is drawn.
        ? `${left} and ${b} ${glyphPlural(glyph) ?? "counters"}`
        : `${(glyphPlural(glyph) ?? "counters").replace(/^./, (c) => c.toUpperCase())} to count`}
      // MA-728 — hidden capture hook, "true"/"false"/absent only.
      data-operand-b-outline={operandBOutlineAttr(markB, operandBDrawn)}>
      {cap && <OperandCaption a={left} b={b} op="+" x={capOffsetX} y={0}
        cardSize={capCardSize} opW={capOpW} />}
      <g transform={`translate(${dotsOffsetX}, ${dotsOffsetY})`}>
        {/* MA-720 RED S-2/S-3 — both groups take the SAME glyph (the second
            group used to ignore it, drawing stars + circles; no served item
            reaches that path today: every star item is single-operand). */}
        <Dots n={left} mark={OPERAND_A_MARK} glyph={glyph} />
        {hasTwo && (
          <>
            <text x={gw(left) + 25} y={h / 2 + 8} textAnchor="middle" fontSize="28" fill="var(--color-text-secondary, #7a6f60)">+</text>
            <g transform={`translate(${gw(left) + 50}, 0)`}><Dots n={b!} mark={markB} startKey={100} glyph={glyph} /></g>
          </>
        )}
      </g>
    </svg>
  );
}

// MA-CLOSURE3 (MA-647 caption probe) — the closed set of concrete-stage
// subtraction visual `kind`s whose renderer draws an `OperandCaption`
// UNCONDITIONALLY whenever it mounts (TakeAwayRenderer.tsx,
// TenFrameSubRenderer.tsx, TwoTenFramesSubRenderer.tsx — each imports
// OperandCaption and renders it with no surrounding conditional). Pinned
// here, alongside the registry, rather than re-typed at each call site.
//
// ⚠️CORRECTED (MA-CLOSURE3 round 2, must-fix 5) — this set is HAND-MAINTAINED,
// not derived from `registeredKeys()` or a source grep of the three
// renderers (an earlier comment here claimed otherwise; that claim was
// false). This set is a/b-INDEPENDENT for these 3 kinds: the renderers
// really do render the caption unconditionally whenever mounted, regardless
// of a/b — so this predicate returning `true` for kind membership alone is
// CORRECT today. What it cannot catch by itself is a FUTURE regression that
// makes one of these renderers suppress the caption under some new
// condition (e.g. a<5) without updating this file — that class of drift is
// caught not by this predicate, but by mathOperandCaptionHook.test.mjs's
// real-render-vs-predicted cross-check (react-dom/server, swept across
// multiple a/b values per kind including a<5) and its live mutation bite
// test, which feeds the REAL rendered outcome — not this predicate's
// verdict — into the MA-647-operand-caption scenario's judge.
export const CONCRETE_SUBTRACTION_CAPTION_KINDS: ReadonlySet<string> = new Set([
  "take_away",
  "ten_frame_sub",
  "two_ten_frames_sub",
  // MA-703 — TakeAwayBlankRenderer.tsx also imports OperandCaption and
  // renders it with no surrounding conditional (one operand always shows
  // as "?" instead of a number, never as no caption at all).
  "take_away_blank",
]);

/**
 * The SAME condition that decides whether the operand caption renders for a
 * given served `kind` — never re-derived per caller. For the three
 * unconditional kinds above, mounting the renderer at all IS the condition
 * (true). For "groups" (the one caption-carrying kind whose renderer
 * branches), this calls `groupsHasTwoOperands` — the EXACT function Groups
 * itself calls — so the two can never drift apart. Every other kind never
 * carries a caption (false).
 *
 * ⚠️CORRECTED (MA-CLOSURE3 round 3, RED must-fix 5): MathSession.tsx's
 * `data-operand-caption` hook still calls this function, but the live-qa
 * capture driver (scripts/live-qa/captureDriver.mjs) no longer trusts this
 * VALUE as the served-surface truth — that was the exact tautology round 3
 * closed (this predicate and the scenario's own PASS/FAIL gate both keyed on
 * kind membership, so a renderer that silently stopped drawing the caption
 * would never be caught). The hook attribute's mere PRESENCE now only marks
 * "a staged math visual is visible here at all" (the driver's ABSTAIN gate,
 * scoping where in the DOM it should even look); the actual shown/absent
 * verdict comes from scanning the RENDERED markup instead. This function
 * itself is unchanged and still legitimately used for offline
 * proofs/simulation (mathOperandCaptionHook.test.mjs, this scenario's own
 * reachability self-test) — just no longer for the live probe's real value.
 */
export function operandCaptionShownForKind(
  kind: string | null | undefined,
  props: Pick<MathRendererProps, "a" | "b">,
): boolean {
  if (!kind) return false;
  if (CONCRETE_SUBTRACTION_CAPTION_KINDS.has(kind)) return true;
  // MA-705 — TwoTenFramesAddRenderer.tsx also imports OperandCaption and
  // renders it with no surrounding conditional (the mapper only ever emits
  // this kind with a real b>0, so unconditional-on-mount and the groups-
  // style b>0 gate coincide here — kept as its own explicit branch, not
  // folded into CONCRETE_SUBTRACTION_CAPTION_KINDS, because that set is
  // documented and named as the subtraction-kind closed set).
  if (kind === "two_ten_frames_add") return true;
  if (kind === "groups") return groupsHasTwoOperands(props);
  return false;
}

/** MA-491: the start is AUTO-REVEALED (always labeled, even off the step
 *  grid) and the jump draws as individually countable hop arcs, arrowhead
 *  on every hop except the last — the landing tick never carries a numeral
 *  or a dot, so it stops being an answer leak (a child could previously
 *  just read the printed sum under the landing dot). The range scales to
 *  the real total instead of a hardcoded max=10, so a sum above ten lands
 *  on the line instead of clamping to the wrong position. Mirrors the
 *  MA-317 fix already shipped for `ScaffoldNL` (src/components/scaffolds/
 *  index.tsx), the same design applied to the sibling HINT-panel
 *  component — kept as a separate implementation here since this inline
 *  surface only ever renders addition (the mapper never routes a
 *  subtraction to `number_line`; see contentToEngineItem.ts) while
 *  ScaffoldNL also has to handle subtraction, so unifying the two would
 *  add prop-shape complexity to this surface for a case it never hits. */
function NumberLine({ a, b, total }: MathRendererProps) {
  const arrowId = useId();
  const markerId = `nl-hop-${arrowId.replace(/[^a-zA-Z0-9]/g, "")}`;
  const startN = a ?? 0;
  const hops = b ?? Math.max(0, total - startN);
  const endN = total;
  const maxN = Math.max(5, Math.ceil(Math.max(startN, endN) / 5) * 5);
  const X0 = 16;
  const X1 = 344;
  const x = (v: number) => X0 + (v / maxN) * (X1 - X0);
  const step = maxN <= 10 ? 1 : 2;
  const ticks: number[] = [];
  for (let n = 0; n <= maxN; n += step) ticks.push(n);
  const label = `Number line. Start at ${startN}.${
    hops > 0 ? ` ${hops} hop${hops === 1 ? "" : "s"} forward.` : ""
  }`;
  return (
    <svg viewBox="0 0 360 70" width="100%" style={{ maxWidth: 360 }} role="img" aria-label={label}>
      <defs>
        <marker id={markerId} markerWidth="8" markerHeight="7" refX="6" refY="3.5" orient="auto">
          <polygon points="0,0 8,3.5 0,7" fill={OPERAND_B_FILL} />
        </marker>
      </defs>
      <line x1={x(0)} y1="46" x2={x(maxN)} y2="46" stroke="var(--color-border-secondary, #cdbfae)" strokeWidth="3" />
      {ticks.map((n) => {
        // The landing tick (the answer) never carries a numeral, even when
        // it coincidentally sits on the step-grid — labeling it would
        // bullseye the answer. The start's numeral renders separately
        // below (auto-revealed regardless of grid alignment); skip it here
        // too so it isn't drawn twice.
        const skipLabel = n === endN || n === startN;
        return (
          <g key={n}>
            <line x1={x(n)} y1="40" x2={x(n)} y2="52" stroke="var(--color-border-secondary, #cdbfae)" strokeWidth="2" />
            {!skipLabel && (
              <text x={x(n)} y="68" textAnchor="middle" fontSize="11" fill="var(--color-text-secondary, #7a6f60)">{n}</text>
            )}
          </g>
        );
      })}
      {/* Countable hops — one arc per unit step, arrowhead on every hop
          EXCEPT the last, so the destination tick is never marked. */}
      {Array.from({ length: hops }).map((_, j) => {
        const from = startN + j;
        const to = from + 1;
        if (to < 0 || to > maxN) return null;
        const fx = x(from);
        const tx = x(to);
        const midX = (fx + tx) / 2;
        const isLast = j === hops - 1;
        return (
          <path
            key={j}
            d={`M${fx},40 Q${midX},${20 - (j % 2) * 4} ${tx},40`}
            stroke={OPERAND_B_FILL}
            strokeWidth="2.4"
            fill="none"
            strokeLinecap="round"
            strokeDasharray={isLast ? "4 4" : undefined}
            markerEnd={isLast ? undefined : `url(#${markerId})`}
          />
        );
      })}
      {/* Start anchor — always labeled, regardless of the tick-grid step,
          so the child has a value to count on from. */}
      <text x={x(startN)} y="68" textAnchor="middle" fontSize="11" fontWeight={700} fill={OPERAND_A_FILL}>{startN}</text>
      <circle cx={x(startN)} cy="46" r="7" fill={OPERAND_A_FILL} />
    </svg>
  );
}

/** The legacy fallback renderer. Historically MathVisual's inline switch
 *  used `groups` as the default (else) branch for any non-ten_frame,
 *  non-number_line kind. Exported so MathVisual can preserve that exact
 *  fail-safe behavior when a kind has no explicit registration. */
export const FALLBACK_RENDERER: MathRenderer = Groups;

// ── MA-C10 (ADR-024 fast-follow) — answer-safe two-ten-frames renderer ──
// Draws two stacked ten-frames (20 cells total), fills exactly `a` cells
// (= the known addend), and shows the total as a numeral label ("= N").
// Routed ONLY when total < 20, so the empty-cell count (20 - a) never
// equals the answer (= total - a < 20 - a). Answer-safety: filled = a,
// not the answer; the child counts forward from the filled cells to the
// numeral total.
function TwoTenFramesMissingAddend({ a, total }: MathRendererProps) {
  const filled = Math.max(0, Math.min(20, a ?? 0));
  const CELL = 68;
  const PITCH = 70; // CELL + 2px gap
  const COLS = 5;
  const PAD = 6;
  const FRAME_GAP = 16; // vertical gap between the two ten-frames
  // MA-665 (decision 3): the label is the founder's screenshot complaint —
  // "14 is very small making it hard to understand the task" — so it grows
  // from 22px to 40px, bold, and LABEL_H grows with it (56, up from 36) so
  // the bigger glyphs never clip against the SVG's viewBox. W is unchanged
  // (label width doesn't affect frame layout) and stays ≤ 362.
  const LABEL_H = 56;
  const W = COLS * PITCH + 2 * PAD; // 362 → fits in 360
  const H = 4 * PITCH + 2 * PAD + FRAME_GAP + LABEL_H; // 2 rows×2 frames + label
  const cellX = (i: number) => PAD + (i % COLS) * PITCH;
  const cellY = (i: number) => {
    const frame = Math.floor(i / 10);
    const rowInFrame = Math.floor((i % 10) / COLS);
    return PAD + frame * (2 * PITCH + FRAME_GAP) + rowInFrame * PITCH;
  };
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxWidth: W }} role="img"
      aria-label={`Two ten-frames. ${filled} filled. Total is ${total}.`}>
      {Array.from({ length: 20 }).map((_, i) => {
        const x = cellX(i);
        const y = cellY(i);
        return (
          <g key={i}>
            <rect x={x} y={y} width={CELL} height={CELL} rx="8"
              fill="none" stroke="var(--color-border-secondary, #cdbfae)" strokeWidth="2" />
            {i < filled && (
              <circle cx={x + CELL / 2} cy={y + CELL / 2} r="22" {...OPERAND_A_MARK} />
            )}
          </g>
        );
      })}
      <text
        x={W / 2}
        y={4 * PITCH + 2 * PAD + FRAME_GAP + LABEL_H - 12}
        textAnchor="middle"
        fontSize="40"
        fontFamily="inherit"
        fontWeight="700"
        fill="var(--color-text-primary, #3d3228)"
      >{`= ${total}`}</text>
    </svg>
  );
}

// ── MA-654 — answer-safe (BY DESIGN) two-stacked-ten-frames teen-number
// reader. Draws two stacked ten-frames (20 cells): the first ALWAYS fully
// filled (10 dots, color A — "one ten"), the second filled with exactly `b`
// dots (color B — "and this many ones"). NO numeral label — unlike sibling
// `TwoTenFramesMissingAddend`'s "= N", this picture is read-the-frames, not
// count-forward-to-a-shown-total.
//
// Answer-safety note (NOT the missing-addend renderer's contract): this
// picture carries the answer BY DESIGN for all three ms.math.13 prompts
// ("What number is shown?" / "How many ones are with the ten?" / "How many
// tens are in this teen number?") — reading a teen number as ten-and-ones
// IS the skill being assessed, the same "the visual IS the question" class
// the `base_ten_blocks` siblings already ship via `place_value_blocks`
// (rendererRegistry.tsx / contentToEngineItem.ts's MA-94 header). The mapper
// (contentToEngineItem.ts, MA-654 branch) only emits this kind when the
// authored firstFrameFilled/secondFrameFilled faithfully reconstruct the
// answer for the item's own focus — this component just draws exactly what
// it is handed.
function TwoTenFramesTeen({ a, b }: MathRendererProps) {
  // MA-654 RED round 1 — fail-safe against NaN (not just null/undefined):
  // `a ?? 10` alone lets a NaN `a` propagate through Math.min/max (NaN is
  // neither null nor undefined, so `??` does not substitute), which would
  // then print literally as "NaN" in the aria-label built below (the old
  // count-free label never had this exposure — it had no interpolation).
  const filled = Math.max(0, Math.min(10, Number.isFinite(a) ? (a as number) : 10));
  // MA-654 RED round 1 (nit 2): clamped to 9, not 10 — the mapper and
  // VISUAL_CONTRACTS.two_ten_frames_teen both cap the ones frame at 9 (a
  // "teen" number is 10..19); a hand-built b=10 payload must not draw a
  // second full frame (which would read as "20" under a kind named teen).
  const filledOnes = Math.max(0, Math.min(9, Number.isFinite(b) ? (b as number) : 0));
  // MA-728 — the mark the ones frame is drawn with (read by the hidden
  // data-operand-b-outline attribute below); it draws `filledOnes` marks.
  const markB = OPERAND_B_MARK;
  const CELL = 68;
  const PITCH = 70; // CELL + 2px gap
  const COLS = 5;
  const PAD = 6;
  const FRAME_GAP = 16; // vertical gap between the two ten-frames
  const W = COLS * PITCH + 2 * PAD; // 362 → fits in 360
  const H = 4 * PITCH + 2 * PAD + FRAME_GAP;
  const cellX = (i: number) => PAD + (i % COLS) * PITCH;
  const cellY = (i: number) => {
    const frame = Math.floor(i / 10);
    const rowInFrame = Math.floor((i % 10) / COLS);
    return PAD + frame * (2 * PITCH + FRAME_GAP) + rowInFrame * PITCH;
  };
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxWidth: W }} role="img"
      // MA-654 RED round 1 — NOT the MA-116 RED-5 count-free pattern. RED-5
      // scopes itself to an OTHERWISE ANSWER-SAFE picture (a subitizing/
      // counting frame where the drawn count is what the child must
      // produce) — count-free there closes a real answer leak. This
      // renderer is the opposite case: it is answer-VISIBLE BY DESIGN (see
      // the header comment above) for all three ms.math.13 prompts, the
      // exact same class as the `base_ten_blocks` sibling
      // (`PlaceValueBlocksRenderer`'s aria-label spells out "N hundreds, N
      // tens, N ones"; `NumeralCardRenderer`'s spells out the numeral).
      // These items carry `supports: []` and no `visualScaffold` — aria is
      // the ONLY channel an AT user has, so withholding the structure here
      // buys zero answer-safety (a sighted child already sees it) and
      // costs the AT child the item entirely. Name the structure, matching
      // the base_ten_blocks sibling's own precedent.
      aria-label={`Two ten frames. The first ten frame is full. The second has ${filledOnes} counters.`}
      // MA-728 — hidden capture hook, "true"/"false"/absent only.
      data-operand-b-outline={operandBOutlineAttr(markB, filledOnes)}>
      {Array.from({ length: 20 }).map((_, i) => {
        const x = cellX(i);
        const y = cellY(i);
        const isFirstFrame = i < 10;
        const filledHere = isFirstFrame ? i < filled : (i - 10) < filledOnes;
        return (
          <g key={i}>
            <rect x={x} y={y} width={CELL} height={CELL} rx="8"
              fill="none" stroke="var(--color-border-secondary, #cdbfae)" strokeWidth="2" />
            {filledHere && (
              <circle cx={x + CELL / 2} cy={y + CELL / 2} r="22" {...(isFirstFrame ? OPERAND_A_MARK : markB)} />
            )}
          </g>
        );
      })}
    </svg>
  );
}

registerRenderer("ten_frame", TenFrame, { label: "Ten frame" });
registerRenderer("number_line", NumberLine, { label: "Number line" });
registerRenderer("groups", Groups, { label: "Counter groups" });

// ── Phase 2 concept visuals (additive) ──
registerRenderer("array", ArrayRenderer, { label: "Array model" });
registerRenderer("place_value_blocks", PlaceValueBlocksRenderer, {
  label: "Place value blocks",
});
// ── MA-C14 — side-by-side place-value comparison renderer ──
registerRenderer("place_value_comparison", PlaceValueComparisonRenderer, {
  label: "Place value comparison",
});

// ── Phase 3 concept visual (additive) ──
registerRenderer("fraction_bar", FractionBarRenderer, { label: "Fraction bar" });

// ── MA-C15 (ADR-067) — multi-bar fraction comparison renderer ──
registerRenderer("fraction_comparison", FractionComparisonRenderer, {
  label: "Fraction comparison",
});

// ── Phase 4 concept visuals (additive) — clock (time) + coins (money) ──
registerRenderer("clock", ClockRenderer, { label: "Clock face" });
registerRenderer("coins", CoinsRenderer, { label: "Coins" });

// ── MA-C17 — elapsed-time (start/end) clock renderer (additive) ──
registerRenderer("elapsed_time_clock", ElapsedTimeClockRenderer, {
  label: "Elapsed time (two clocks)",
});

// ── MA-C10 (ADR-024 fast-follow) — answer-safe missing-addend ten-frames ──
registerRenderer("two_ten_frames_missing_addend", TwoTenFramesMissingAddend, {
  label: "Two ten-frames (missing addend)",
});

// ── MA-654 — answer-visible-by-design teen-number ten-frame reader ──
registerRenderer("two_ten_frames_teen", TwoTenFramesTeen, {
  label: "Two ten-frames (teen number)",
});

// ── MA-C9 — take-away subtraction visual (additive) ──
registerRenderer("take_away", TakeAwayRenderer, { label: "Take-away subtraction" });
registerRenderer("take_away_blank", TakeAwayBlankRenderer, { label: "Take-away subtraction (blank slot)" });
registerRenderer("number_bond", NumberBondRenderer, { label: "Number bond" });
// MA-690 (slice ms-math-05-family-pictures) — answer-safe part-whole pictures
// for a stimulus-sourced missing_addend model authored as split_ten_frame /
// two_set_visual / missing_addend_strip (contentToEngineItem.ts). Each draws
// the whole (numeral) and the known part (counters) and ONE "?" element for
// the missing part; none reads `b`. Not staged (mathVisualStage.ts): the
// authored model renders unchanged at every band, like `number_bond`.
registerRenderer("split_ten_frame", SplitTenFrameRenderer, { label: "Split ten frame (part hidden)" });
registerRenderer("set_picture", SetPictureRenderer, { label: "Two sets (part hidden)" });
registerRenderer("part_strip", PartStripRenderer, { label: "Part strip (part hidden)" });

// ── MA-655 — authored-subtraction visuals routed BEFORE the generic
// take_away arm (additive; see contentToEngineItem.ts's MA-655 routing
// block and docs/build/reports/MA-651-batch1-take-away-2026-09-14.md) ──
registerRenderer("ten_frame_sub", TenFrameSubRenderer, { label: "Ten frame (subtraction)" });
registerRenderer("two_ten_frames_sub", TwoTenFramesSubRenderer, {
  label: "Two ten-frames (subtraction)",
});
// ── MA-705 — two_ten_frames-authored ADDITION (the ms.math.11 make-ten
// twins of the subtraction arm just above; see contentToEngineItem.ts's
// MA-705 routing block) ──
registerRenderer("two_ten_frames_add", TwoTenFramesAddRenderer, {
  label: "Two ten-frames (addition)",
});
// ── MA-707 — two-digit addition as base-ten blocks in tens/ones columns,
// sum row empty (ms.math.26 `addition_regroup`; see contentToEngineItem.ts's
// MA-707 routing block and mathVisualStage.ts's COLUMN_ADD_KINDS rule) ──
registerRenderer("column_add", ColumnAddRenderer, {
  label: "Column addition (base-ten blocks)",
});
// ── MA-706 — skip-counting number track (see contentToEngineItem.ts's
// MA-706 routing block: skip_count_next/offset_next/missing_term/reverse) ──
registerRenderer("skip_count_line", SkipCountLineRenderer, {
  label: "Skip-count number track",
});
// `take_away_line` is a mapper-only kind (never the child's ambient default
// picture — mathVisualStage.ts's ALWAYS_PROJECTED_KINDS projects it to
// count_back at every band before a session screen renders it). Registered
// as an alias to CountBackRenderer anyway, fail-closed style: any future
// caller that resolves a renderer straight off a raw (unstaged) mathVisual
// — a preview surface, a test harness — still gets the correct picture
// instead of a missing-renderer fallback.
registerRenderer("take_away_line", CountBackRenderer, {
  label: "Take-away (number line authored — alias of count_back)",
});
// MA-697 — `count_on_line`: the addition twin of `take_away_line` for a
// stimulus-sourced, number_line-authored addition ("Start at 5. Jump forward
// 3."). Unlike take_away_line it is its OWN concrete picture (a line with a
// start marker, ticks and hops, no numeral past the start — see
// CountOnLineRenderer.tsx); mathVisualStage.ts stages it to count_on at
// bridging and equation at abstract, like its non-jump `groups` siblings.
registerRenderer("count_on_line", CountOnLineRenderer, {
  label: "Count on (number line jump)",
});

// ── MA-C12 — division-as-sharing partition visual (additive) ──
registerRenderer("division_sharing", DivisionSharingRenderer, { label: "Division sharing" });

// ── MA-C13 — equal-groups discrete-clusters visual (additive) ──
registerRenderer("discrete_clusters", DiscreteClustersRenderer, { label: "Discrete clusters" });

// MA-C18: dot/subitize pattern renderer (dice / five-frame / generic dot cluster).
registerRenderer("dot_pattern", DotPatternRenderer, { label: "Dot pattern (subitize)" });

// MA-C19: numeral card/glyph renderer (read-the-numeral — the visual IS the question).
registerRenderer("numeral_card", NumeralCardRenderer, { label: "Numeral card" });

// MA-C20: two_color_ten_frame make-ten renderer (ONE part-whole ten-frame;
// distinct from the two-frame two_ten_frames_missing_addend above).
registerRenderer("two_color_ten_frame", TwoColorTenFrameRenderer, { label: "Ten frame (make ten)" });

// MA-C21: shape identification card (read-the-shape) + pattern-continuation
// strip (shown sequence + a never-answer-revealing "next" tile).
registerRenderer("shape_array", ShapeArrayRenderer, { label: "Shape card" });
registerRenderer("pattern_strip", PatternStripRenderer, { label: "Pattern strip" });

// MA-625 — staged add/sub renderers (mathVisualStage.ts projects a
// groups/ten_frame/take_away mathVisual to one of these three kinds at
// serve time by the child's mastery band; never emitted by the mapper).
registerRenderer("count_on", CountOnRenderer, { label: "Count on (numeral + dots)" });
registerRenderer("count_back", CountBackRenderer, { label: "Count back (numeral + crossed dots)" });
registerRenderer("equation", EquationRenderer, { label: "Equation (numeral cards, no dots)" });
